/* processed: techdebt-pass 2026-01-25 domain=frontend rule=remove-console-logs */
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  GambitSidebar,
  AgentChannelState,
  AgentType,
} from "../components/GambitSidebar";
import Eisenboard, { Facet, Domain } from "../components/Eisenboard";
import { StratumDiagnosis } from "../components/StratumDiagnosis";
import { GambitChatWidget } from "../components/GambitChatWidget";
import { AGENTS, AgentId } from "../config/agents";
import { sendAnalyticsEvent } from "../lib/analytics";
import { sendFunnelEvent } from "../lib/funnel";
import { buildAuthHeader } from "../lib/authEmail";
import {
  EISENBOARD_FACETS_KEY,
  isTimeoutErrorText,
} from "../lib/sessionProgress";
import {
  saveCrucibleSession,
  loadCrucibleSession,
  clearCrucibleSession,
} from "../lib/crucibleSession";
import { extractNameReply, sanitizeUserName } from "../lib/nameCapture";
import { useAuth } from "../contexts/AuthContext";
import { ProjectPanel } from "../components/ProjectPanel";
import { SnapshotDrawer } from "../components/SnapshotDrawer";
import {
  createProject,
  getProject,
  saveSnapshot,
  type Project as BackendProject,
  type ProjectSnapshot,
} from "../services/projectService";
import { crucible } from "../lib/agents/crucible";

const MESSAGE_TRIM_LENGTH = 180;
const ACCESS_STATE_KEY = "crucible_access_state_v1";
const MANUAL_MODE_KEY = "crucible_manual_mode_v1";
const INITIAL_QUERY_FRAME_MESSAGE =
  "Let's break this down together. I'll map your challenge onto the Eisenboard — urgency vs importance — then loop in specialist agents to dig deeper. Ask follow-ups anytime.";

type AccessState = "anonymous" | "signed_in_nonalpha" | "signed_in_alpha";

const readStoredAccessState = (): AccessState => {
  if (typeof window === "undefined") return "anonymous";
  try {
    const value = window.localStorage?.getItem(ACCESS_STATE_KEY);
    if (
      value === "anonymous" ||
      value === "signed_in_nonalpha" ||
      value === "signed_in_alpha"
    ) {
      return value;
    }
  } catch {
    // ignore storage errors
  }
  return "anonymous";
};

const readStoredManualMode = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage?.getItem(MANUAL_MODE_KEY) === "true";
  } catch {
    return false;
  }
};

const readStoredUserName = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage?.getItem("crucible_user_name") || null;
    const sanitized = sanitizeUserName(raw);
    if (raw && !sanitized) {
      window.localStorage?.removeItem("crucible_user_name");
    }
    return sanitized;
  } catch {
    return null;
  }
};

const CruciblePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signInWithGoogle, sendEmailLink, emailLinkSent } = useAuth();
  const initialQuery = location.state?.initialQuery;
  const hasInitialQuery =
    typeof initialQuery === "string" && initialQuery.trim().length > 0;
  const storedName = readStoredUserName();

  // Board State (Starts Empty for "Tour" Effect)
  const [activeFacets, setActiveFacets] = useState<Facet[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [questionCount, setQuestionCount] = useState<number>(0);
  const [maxQuestions, setMaxQuestions] = useState<number>(5);

  // PRD14: Backend project persistence
  const [backendProjectId, setBackendProjectId] = useState<string | null>(null);
  const [projectSnapshots, setProjectSnapshots] = useState<ProjectSnapshot[]>([]);
  const backendProjectIdRef = React.useRef<string | null>(null);
  backendProjectIdRef.current = backendProjectId;

  // State
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [isGambitOpen, setIsGambitOpen] = useState(false);
  const [handoffComplete, setHandoffComplete] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(storedName);
  const [isAwaitingNameReply, setIsAwaitingNameReply] = useState<boolean>(
    !storedName && !hasInitialQuery,
  );
  const normalizedUserName = sanitizeUserName(userName);

  // Captive Portal State (PRD17: 3-send preview limit)
  const PREVIEW_LIMIT = 3;
  const [userSendCount, setUserSendCount] = useState(0);
  const [showCaptivePortal, setShowCaptivePortal] = useState(false);
  const [captiveEmail, setCaptiveEmail] = useState("");
  const [captiveEmailSubmitted, setCaptiveEmailSubmitted] = useState(false);
  const [captiveSubmitting, setCaptiveSubmitting] = useState(false);
  const [authTab, setAuthTab] = useState<"google" | "email">("google");
  const [otpEmail, setOtpEmail] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [diagnosisDismissed, setDiagnosisDismissed] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [accessState, setAccessState] = useState<AccessState>(() =>
    readStoredAccessState(),
  );
  const [manualModeEnabled, setManualModeEnabled] = useState<boolean>(() =>
    readStoredManualMode(),
  );
  const pendingMessageRef = React.useRef<{
    channelId: AgentType;
    text: string;
  } | null>(null);
  const restoredManualModeRef = React.useRef(readStoredManualMode());
  const pendingAutoHandoffRef = React.useRef<{
    channelId: AgentType;
    timerId: number;
  } | null>(null);
  const requestRevisionRef = React.useRef(0);
  const latestAppliedBoardRevisionRef = React.useRef(0);
  const latestChannelRevisionRef = React.useRef<Record<AgentType, number>>({
    orchestrator: 0,
    people: 0,
    process: 0,
    tech: 0,
    data: 0,
    partner: 0,
  });
  const messageSequenceRef = React.useRef(0);
  // true when we've already asked for (or captured) the user's name
  const hasAskedForName = React.useRef<boolean>(!!storedName || !hasInitialQuery);
  const isTyping = pendingRequestCount > 0;
  const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) || "";
  const manualModeActive = accessState === "signed_in_nonalpha" || manualModeEnabled;

  const lockReason =
    accessState === "signed_in_nonalpha"
      ? "Signed in, awaiting alpha approval — manual board mode is active."
      : "Preview complete — manual board mode is active until you sign in.";

  const persistAccessState = (nextState: AccessState) => {
    setAccessState(nextState);
    try {
      window.localStorage?.setItem(ACCESS_STATE_KEY, nextState);
    } catch {
      // ignore storage errors
    }
  };

  const persistManualMode = (enabled: boolean) => {
    setManualModeEnabled(enabled);
    try {
      if (enabled) {
        window.localStorage?.setItem(MANUAL_MODE_KEY, "true");
      } else {
        window.localStorage?.removeItem(MANUAL_MODE_KEY);
      }
    } catch {
      // ignore storage errors
    }
  };

  const enableManualMode = (reason: string) => {
    if (!manualModeEnabled) {
      sendAnalyticsEvent("auth_gate_manual_mode_entered", {
        route: "/crucible",
        reason,
        access_state: accessState,
      });
      sendAnalyticsEvent("auth_gate_manual_state_saved", {
        route: "/crucible",
        reason,
        access_state: accessState,
      });
    }
    persistManualMode(true);
  };

  const syncAccessState = React.useCallback(async () => {
    try {
      const response = await fetch(`${apiBase}/api/auth/access`, {
        headers: {
          "Content-Type": "application/json",
          ...buildAuthHeader(),
        },
      });
      if (!response.ok) return;

      const payload = (await response.json()) as { state?: AccessState };
      const nextState =
        payload.state === "signed_in_nonalpha" || payload.state === "signed_in_alpha"
          ? payload.state
          : "anonymous";

      persistAccessState(nextState);

      if (nextState === "signed_in_alpha") {
        if (manualModeEnabled || showCaptivePortal) {
          sendAnalyticsEvent("auth_gate_alpha_unlocked", { route: "/crucible" });
        }
        persistManualMode(false);
        setShowCaptivePortal(false);
        setCaptiveEmailSubmitted(false);
        pendingMessageRef.current = null;
        return;
      }

      if (nextState === "signed_in_nonalpha") {
        enableManualMode("signed_in_nonalpha");
      }
    } catch {
      // retain last known state when access checks fail
    }
  }, [apiBase, manualModeEnabled, showCaptivePortal]);

  const [channels, setChannels] = useState<AgentChannelState[]>(
    Object.values(AGENTS).map((agent) => ({
      id: agent.id,
      name: agent.name,
      title: agent.title,
      pitch: agent.pitch,
      isOpen: agent.id === "orchestrator",
      color: agent.colorClass,
      bgColor: agent.bgClass,
      messages:
        agent.id === "orchestrator"
          ? [
              {
                id: "0",
                sender: "agent",
                text: storedName
                  ? `Welcome back, *${storedName}*! I'm *Nova* — ready to run the Crucible. What's on your mind?`
                  : hasInitialQuery
                    ? "Hi, I'm *Nova* — I run the Crucible."
                    : "Hi, I'm *Nova* — I run the Crucible. Before we dive in, what should I call you?",
              },
            ]
          : [],
    })),
  );

  const domainToAgent: Record<string, AgentType> = {
    PEOPLE: "people",
    PROCESS: "process",
    TECH: "tech",
    DATA: "data",
    STRATEGY: "orchestrator",
  };

  const clipMessageText = (value: string): string => {
    const normalized = value.replace(/\s+/g, " ").trim();
    if (normalized.length <= MESSAGE_TRIM_LENGTH) return normalized;
    return `${normalized.slice(0, MESSAGE_TRIM_LENGTH - 3)}...`;
  };

  const createMessageId = () => {
    messageSequenceRef.current += 1;
    return `msg-${Date.now()}-${messageSequenceRef.current}`;
  };

  const beginRequest = (channelId: AgentType) => {
    requestRevisionRef.current += 1;
    const revision = requestRevisionRef.current;
    latestChannelRevisionRef.current[channelId] = revision;
    setPendingRequestCount((count) => count + 1);
    return revision;
  };

  const endRequest = () => {
    setPendingRequestCount((count) => Math.max(0, count - 1));
  };

  const isLatestChannelRevision = (channelId: AgentType, revision: number) =>
    latestChannelRevisionRef.current[channelId] === revision;

  const isLatestGlobalRevision = (revision: number) =>
    requestRevisionRef.current === revision;

  const clearPendingAutoHandoff = () => {
    if (pendingAutoHandoffRef.current) {
      window.clearTimeout(pendingAutoHandoffRef.current.timerId);
      pendingAutoHandoffRef.current = null;
    }
  };

  const mapCardsToFacets = (
    cards?: Array<{
      id: string;
      type: string;
      title: string;
      description: string;
      urgency?: number;
      importance?: number;
      aiAnalysis?: string[];
    }>,
  ): Facet[] =>
    Array.isArray(cards)
      ? cards.map((card) => ({
          id: card.id,
          domain: card.type as Domain,
          title: card.title,
          summary: card.description,
          urgency: card.urgency ?? 5,
          importance: card.importance ?? 5,
          aiAnalysis: card.aiAnalysis || [],
        }))
      : [];

  const applyUpdatedDoc = (
    updatedDoc: {
      cards?: Array<{
        id: string;
        type: string;
        title: string;
        description: string;
        urgency?: number;
        importance?: number;
        aiAnalysis?: string[];
      }>;
      meta?: { questionCount?: number; maxQuestions?: number };
    },
    requestRevision: number,
  ) => {
    if (requestRevision < latestAppliedBoardRevisionRef.current) return false;

    latestAppliedBoardRevisionRef.current = requestRevision;
    setQuestionCount(updatedDoc?.meta?.questionCount ?? 0);
    setMaxQuestions(updatedDoc?.meta?.maxQuestions ?? 5);
    const incoming = mapCardsToFacets(updatedDoc?.cards);
    setActiveFacets((current) => {
      if (current.length === 0) return incoming;
      const currentIds = new Set(current.map((f) => f.id));
      const updated = current.map((c) => incoming.find((n) => n.id === c.id) ?? c);
      const added = incoming.filter((n) => !currentIds.has(n.id));
      return [...updated, ...added];
    });
    return true;
  };

  const buildSpecialistContext = (
    channelId: AgentType,
    input: string,
    workingDoc: {
      constraints?: { primary?: string };
      hypotheses?: string[];
      openQuestions?: string[];
    },
    orchestratorPrompt?: string,
  ) => {
    const agent = AGENTS[channelId as AgentId];
    const recentContext = channels
      .filter(
        (channel) => channel.id === "orchestrator" || channel.id === channelId,
      )
      .flatMap((channel) =>
        channel.messages.map((message) => {
          const speaker =
            message.sender === "user"
              ? "User"
              : message.fromAgent
                ? (AGENTS[message.fromAgent as AgentId]?.name ?? channel.name)
                : channel.name;

          return `${speaker}: ${clipMessageText(message.text)}`;
        }),
      )
      .slice(-6);

    const boardSignals = activeFacets
      .filter((facet) => domainToAgent[facet.domain] === channelId)
      .slice(0, 3)
      .map(
        (facet) =>
          `${facet.title} (U${facet.urgency ?? 5}/I${facet.importance ?? 5})`,
      );

    return [
      `Continue the existing Crucible conversation as ${agent.name} (${agent.title}).`,
      agent.systemPrompt,
      `Stay focused on ${agent.role.toLowerCase()} and do not restart intake.`,
      normalizedUserName ? `The user's name is ${normalizedUserName}. Address them by name occasionally (not every message).` : '',
      `Do not repeat broad questions that Nova already covered unless the user contradicted themselves.`,
      orchestratorPrompt
        ? `Nova's latest question: ${clipMessageText(orchestratorPrompt)}`
        : "",
      recentContext.length
        ? `Recent context:\n- ${recentContext.join("\n- ")}`
        : "",
      boardSignals.length
        ? `Current board signals in your lane:\n- ${boardSignals.join("\n- ")}`
        : "",
      workingDoc.constraints?.primary
        ? `Current working constraint: ${workingDoc.constraints.primary}.`
        : "",
      workingDoc.hypotheses?.[0]
        ? `Current working hypothesis: ${clipMessageText(workingDoc.hypotheses[0])}`
        : "",
      workingDoc.openQuestions?.[0]
        ? `Open question to build from: ${clipMessageText(workingDoc.openQuestions[0])}`
        : "",
      `Latest user message: ${clipMessageText(input)}`,
      "Respond with one grounded observation and at most one non-redundant follow-up question.",
    ]
      .filter(Boolean)
      .join("\n\n");
  };

  const latestAgentMessage = (channelId: AgentType) =>
    [...(channels.find((channel) => channel.id === channelId)?.messages ?? [])]
      .reverse()
      .find((message) => message.sender === "agent")?.text;

  const pickSpecialists = (doc: {
    cards?: Array<{ type?: string; urgency?: number; importance?: number }>;
  }): AgentType[] => {
    const cards = Array.isArray(doc?.cards) ? doc.cards : [];
    if (!cards.length) return [];
    const sorted = [...cards].sort(
      (a, b) =>
        (b.urgency ?? 5) +
        (b.importance ?? 5) -
        ((a.urgency ?? 5) + (a.importance ?? 5)),
    );
    const seen = new Set<AgentType>();
    for (const card of sorted) {
      const target = domainToAgent[String(card.type || "").toUpperCase()];
      if (target && target !== "orchestrator" && !seen.has(target)) {
        seen.add(target);
      }
    }
    return Array.from(seen);
  };

  const hasInitialized = React.useRef(false);
  const hasRestoredSession = React.useRef(false);
  const isRestoredSession = React.useRef(false);

  useEffect(() => {
    sendAnalyticsEvent("crucible.boot");
    if (restoredManualModeRef.current) {
      sendAnalyticsEvent("auth_gate_manual_state_restored", {
        route: "/crucible",
        access_state: accessState,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isHydrated) sendAnalyticsEvent("crucible.hydration_complete");
  }, [isHydrated]);

  useEffect(() => {
    void syncAccessState();
  }, [syncAccessState, user?.email]);

  useEffect(() => {
    if (userName === normalizedUserName) return;
    setUserName(normalizedUserName);
    try {
      if (normalizedUserName) {
        window.localStorage?.setItem("crucible_user_name", normalizedUserName);
      } else {
        window.localStorage?.removeItem("crucible_user_name");
      }
    } catch {
      // ignore storage errors
    }
  }, [normalizedUserName, userName]);

  // Restore saved guest session on mount (before initial-query effect)
  useEffect(() => {
    if (hasRestoredSession.current) return;
    hasRestoredSession.current = true;

    const saved = loadCrucibleSession();
    if (!saved) {
      setIsHydrated(true);
      return;
    }

    // If arriving with a fresh initialQuery, skip restore — start clean
    if (initialQuery) {
      clearCrucibleSession();
      setIsHydrated(true);
      return;
    }

    // Restore channel messages (merge onto agent config defaults)
    setChannels((prev) =>
      prev.map((ch) => {
        const savedCh = saved.channels.find((s) => s.id === ch.id);
        if (!savedCh || savedCh.messages.length === 0) return ch;
        return {
          ...ch,
          messages: savedCh.messages.map((m) => ({
            id: m.id,
            sender: m.sender,
            text: m.text,
            variant: m.variant,
            fromAgent: m.fromAgent,
          })),
          isOpen: savedCh.isOpen,
        };
      }),
    );

    // Restore Eisenboard facets — flag so PRD14 persist skips the mount-restore write
    isRestoredSession.current = true;
    if (saved.facets.length) setActiveFacets(saved.facets);

    // Restore captive portal / orchestration state
    setUserSendCount(saved.userSendCount);
    setCaptiveEmailSubmitted(saved.captiveEmailSubmitted);
    setHandoffComplete(saved.handoffComplete);
    setQuestionCount(saved.questionCount);
    setMaxQuestions(saved.maxQuestions);
    if (saved.userName && !normalizedUserName) {
      setUserName(saved.userName);
      setIsAwaitingNameReply(false);
      try { window.localStorage?.setItem('crucible_user_name', saved.userName); } catch { /* ignore */ }
    }

    // Restore CrucibleEngine working doc so follow-ups have context
    if (saved.workingDoc) {
      crucible.restore({
        ...saved.workingDoc,
        conversationHistory: saved.conversationHistory,
      });
    }

    // Mark as already initialized so the initialQuery effect won't fire
    hasInitialized.current = true;
    setIsHydrated(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save session to localStorage on meaningful state changes
  useEffect(() => {
    // Don't save until at least one message exists beyond the default greeting
    const totalMessages = channels.reduce(
      (sum, ch) => sum + ch.messages.length,
      0,
    );
    if (totalMessages <= 1) return;

    const timeout = setTimeout(() => {
      saveCrucibleSession({
        savedAt: new Date().toISOString(),
        channels: channels.map((ch) => ({
          id: ch.id,
          messages: ch.messages.map((m) => ({
            id: m.id,
            sender: m.sender,
            text: m.text,
            variant: m.variant,
            fromAgent: m.fromAgent,
          })),
          isOpen: ch.isOpen,
        })),
        facets: activeFacets,
        userSendCount,
        captiveEmailSubmitted,
        handoffComplete,
        questionCount,
        maxQuestions,
        workingDoc: crucible.getWorkingDoc(),
        conversationHistory: crucible.getConversationHistory(),
        userName: normalizedUserName ?? undefined,
      });
    }, 300); // debounce 300ms

    return () => clearTimeout(timeout);
  }, [
    channels,
    activeFacets,
    userSendCount,
    captiveEmailSubmitted,
    handoffComplete,
    questionCount,
    maxQuestions,
    normalizedUserName,
    userName,
  ]);

  // Handle Initial Query from Landing Page
  useEffect(() => {
    if (initialQuery && !hasInitialized.current) {
      hasInitialized.current = true;
      setIsAwaitingNameReply(false);

      // Track analytics: User opened Gambit from landing page
      sendAnalyticsEvent("gambit_open", { source: "landing" });
      void sendFunnelEvent({ type: "crucible_load", email: "", source: "landing" });

      // 1. Nova frames how she'll handle the carried-over landing-page prompt.
      addMessage(
        "orchestrator",
        "agent",
        INITIAL_QUERY_FRAME_MESSAGE,
        200,
        15,
      );

      // 2. Show the carried-over landing-page input after Nova's framing.
      addMessage("orchestrator", "user", initialQuery, 900, 0);

      // 3. Trigger AI processing after the carried-over input is visible.
      setTimeout(() => {
        processAIResponse(initialQuery);
      }, 1500);
    }
  }, [initialQuery]);

  useEffect(() => {
    if (activeFacets.length === 0) return;
    try {
      sessionStorage.setItem(
        EISENBOARD_FACETS_KEY,
        JSON.stringify(activeFacets),
      );
    } catch {
      // ignore storage errors
    }
  }, [activeFacets]);

  // PRD14: best-effort backend persist on facet change — skip on session restore
  useEffect(() => {
    if (activeFacets.length === 0) return;
    if (isRestoredSession.current) {
      isRestoredSession.current = false;
      return;
    }
    const authH = buildAuthHeader();
    const facetsForBackend = activeFacets.map((f) => ({
      id: f.id,
      title: f.title,
      summary: f.summary,
      quadrant: "",
      root: false,
      domain: f.domain ?? "",
      urgency: f.urgency,
      importance: f.importance,
    }));
    (async () => {
      try {
        let projId = backendProjectIdRef.current;
        if (!projId) {
          const p: BackendProject = await createProject("Crucible Session", authH);
          projId = p.id;
          setBackendProjectId(p.id);
          setProjectSnapshots(p.snapshots ?? []);
        }
        const updated: BackendProject = await saveSnapshot(projId, facetsForBackend, "Crucible", authH);
        setProjectSnapshots(updated.snapshots ?? []);
      } catch {
        // best-effort; silently ignore
      }
    })();
  }, [activeFacets]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearPendingAutoHandoff(), []);

  // Captive Portal email submission
  const handleCaptiveSubmit = async () => {
    if (!captiveEmail || !captiveEmail.includes("@")) return;
    setCaptiveSubmitting(true);
    setApiError(null);
    try {
      await sendFunnelEvent(
        {
          type: "email_capture",
          email: captiveEmail.trim(),
          route: "/crucible",
          source: "crucible_captive_portal",
          reason: "preview_limit",
          sessionId: "crucible",
          meta: {
            preview_limit: PREVIEW_LIMIT,
            queued_channel: pendingMessageRef.current?.channelId || "",
          },
        },
        { throwOnError: true },
      );
      setCaptiveEmailSubmitted(true);
      enableManualMode("email_capture");
      sendAnalyticsEvent("captive_portal_submit", { route: "/crucible" });
      pendingMessageRef.current = null;
    } catch (error) {
      setApiError(
        error instanceof Error
          ? error.message
          : "Email capture failed. Try again.",
      );
    } finally {
      setCaptiveSubmitting(false);
    }
  };

  // Main Orchestration Logic (Real Backend)
  const handleSendMessage = async (channelId: AgentType, text: string) => {
    const requiresAlpha = accessState !== "signed_in_alpha";
    const nextSendCount = userSendCount + 1;

    if (accessState === "signed_in_nonalpha") {
      pendingMessageRef.current = null;
      enableManualMode("signed_in_nonalpha");
      setShowCaptivePortal(true);
      sendAnalyticsEvent("auth_gate_hard_block_shown", {
        route: "/crucible",
        reason: "signed_in_nonalpha",
      });
      sendAnalyticsEvent("auth_gate_send_blocked", {
        route: "/crucible",
        reason: "signed_in_nonalpha",
        channel: channelId,
      });
      return;
    }

    if (requiresAlpha && nextSendCount > PREVIEW_LIMIT) {
      pendingMessageRef.current = { channelId, text };
      enableManualMode("preview_limit");
      setShowCaptivePortal(true);
      sendAnalyticsEvent("auth_gate_hard_block_shown", {
        sendCount: nextSendCount,
        route: "/crucible",
        reason: "preview_limit",
      });
      sendAnalyticsEvent("auth_gate_send_blocked", {
        sendCount: nextSendCount,
        route: "/crucible",
        reason: "preview_limit",
        channel: channelId,
      });
      return;
    }
    setUserSendCount(nextSendCount);
    void sendFunnelEvent({ type: "question_asked", email: "", source: "crucible", meta: { sendCount: nextSendCount, channel: channelId } });

    // 1. Add User Message (Instant)
    addMessage(channelId, "user", text, 0, 0);

    // 2. Trigger AI
    if (channelId === "orchestrator") {
      sendAnalyticsEvent("nova_message");
      await processAIResponse(text);
    } else if (channelId !== "partner") {
      // Specialist channel: route through crucible with specialist context
      sendAnalyticsEvent("specialist_message", { specialist: channelId });
      await processSpecialistResponse(channelId, text);
    }
  };

  const processAIResponse = async (input: string) => {
    // Only capture names from dedicated replies, never from arbitrary landing-page prompts.
    if (!normalizedUserName) {
      const name = extractNameReply(input, isAwaitingNameReply);
      if (name) {
        setUserName(name);
        setIsAwaitingNameReply(false);
        try { window.localStorage?.setItem('crucible_user_name', name); } catch { /* ignore */ }
        addMessage(
          "orchestrator",
          "agent",
          `Great to meet you, *${name}*! I work with *Gambit*, the purple android on the bottom right. Now — what's your biggest challenge right now? Tell me what keeps you up at night.`,
          300,
          12,
        );
        return;
      }

      if (isAwaitingNameReply) {
        setIsAwaitingNameReply(false);
      }
    }

    const requestRevision = beginRequest("orchestrator");
    try {
      const result = await crucible.processInput(input, "PARTNER", {
        agentContext: [
          AGENTS.orchestrator.systemPrompt,
          normalizedUserName ? `The user's name is ${normalizedUserName}. Address them by name occasionally to keep it personal.` : '',
        ].filter(Boolean).join('\n\n'),
      });

      if (result.error && isTimeoutErrorText(String(result.error))) {
        navigate("/", {
          replace: true,
          state: {
            timedOut: true,
            timeoutReason: String(result.error),
          },
        });
        return;
      }

      if (!isLatestChannelRevision("orchestrator", requestRevision)) {
        return;
      }

      setApiError(result.error ? String(result.error) : null);

      applyUpdatedDoc(result.updatedDoc, requestRevision);

      // Update Chat
      addMessage(
        "orchestrator",
        "agent",
        result.response,
        500,
        10,
        result.error ? "error" : undefined,
        undefined,
        requestRevision,
      ); // Slight delay for "thinking" feel
      if (!result.error) sendAnalyticsEvent("beat.complete", { channel: "orchestrator", questionCount });

      // After first real response, ask for name if we still don't know it
      if (!normalizedUserName && !hasAskedForName.current && !result.error) {
        hasAskedForName.current = true;
        setIsAwaitingNameReply(true);
        addMessage(
          "orchestrator",
          "agent",
          "One more thing — what should I call you?",
          1800,
          8,
        );
      }

      const readyToHandoff =
        isLatestGlobalRevision(requestRevision) &&
        !handoffComplete &&
        (result.updatedDoc?.meta?.questionCount ?? 0) >= 2;
      if (readyToHandoff) {
        const targets = pickSpecialists(result.updatedDoc);
        if (targets.length > 0) {
          clearPendingAutoHandoff();

          // Build Nova's handoff announcement with colored agent names
          const namedList = targets.map((t) => {
            const a = AGENTS[t as AgentId];
            return `*${a.name}* (${a.title})`;
          });
          const handoffLine =
            targets.length === 1
              ? `I'm looping in ${namedList[0]} to take first pass on ${AGENTS[targets[0] as AgentId].role.toLowerCase()} while I keep orchestration aligned.`
              : `I'm splitting this across ${namedList.slice(0, -1).join(", ")} and ${namedList[namedList.length - 1]}. Each specialist will pressure-test their domain while I keep orchestration aligned.`;

          addMessage(
            "orchestrator",
            "agent",
            handoffLine,
            300,
            10,
            undefined,
            undefined,
            requestRevision,
          );

          // Tailored handoff messages per specialist
          const n = normalizedUserName ? `, ${normalizedUserName},` : '';
          const specialistIntros: Record<string, string> = {
            people: `*Nova* → *Mira*: I see trust and ownership signals in these cards. ${normalizedUserName ?? 'The user'} needs help with the people angle — where are the relationship fractures or accountability gaps hiding?`,
            process: `*Nova* → *Rowan*: There are workflow and handoff issues here. Map the friction${n} — which steps, rituals, or handoffs are adding drag without adding value?`,
            tech: `*Nova* → *Devon*: I'm seeing structural and systems risks. Dig into the technical debt and architecture — what's fragile, what compounds, and what should we stop building on?`,
            data: `*Nova* → *Naomi*: We need evidence before opinions. What should we be measuring, what signals are missing, and what number would force this decision out of guesswork?`,
          };

          // Open channels and post tailored intros
          setChannels((prev) =>
            prev.map((c) =>
              targets.includes(c.id) ? { ...c, isOpen: c.id === targets[0] } : c,
            ),
          );

          targets.forEach((target, idx) => {
            const intro =
              specialistIntros[target] ??
              `*Nova* → *${AGENTS[target as AgentId].name}*: please lead with a quick breakdown on ${AGENTS[target as AgentId].pitch.toLowerCase()}.`;
            addMessage(
              target,
              "agent",
              intro,
              600 + idx * 400,
              12,
              undefined,
              "orchestrator",
            );
          });

          setHandoffComplete(true);
          sendAnalyticsEvent("arc.resolve", { specialists: targets.length, channels: targets });

          // Auto-trigger ALL specialists to respond with staggered delays
          const seed = input.trim();
          if (seed) {
            targets.forEach((target, idx) => {
              const timerId = window.setTimeout(() => {
                if (idx === 0) pendingAutoHandoffRef.current = null;
                void processSpecialistResponse(
                  target,
                  seed,
                  result.response,
                );
              }, 900 + idx * 1200);
              // Track only the first for cleanup purposes
              if (idx === 0) {
                pendingAutoHandoffRef.current = {
                  channelId: target,
                  timerId,
                };
              }
            });
          }
        }
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      if (isTimeoutErrorText(msg)) {
        navigate("/", {
          replace: true,
          state: {
            timedOut: true,
            timeoutReason: msg,
          },
        });
        return;
      }
      if (isLatestChannelRevision("orchestrator", requestRevision)) {
        sendAnalyticsEvent("plot_hole.detected", { channel: "orchestrator", error: msg });
        setApiError(msg);
        addMessage(
          "orchestrator",
          "agent",
          `⚠️ ${msg}`,
          0,
          10,
          "error",
          undefined,
          requestRevision,
        );
      }
    } finally {
      endRequest();
    }
  };

  // Process messages sent to specialist channels (Rowan, Mira, Devon, Naomi)
  const processSpecialistResponse = async (
    channelId: AgentType,
    input: string,
    orchestratorPrompt?: string,
  ) => {
    const requestRevision = beginRequest(channelId);
    try {
      // Build rich specialist context with board signals & recent conversation
      const agentContext = buildSpecialistContext(
        channelId,
        input,
        crucible.getWorkingDoc(),
        orchestratorPrompt ?? latestAgentMessage("orchestrator"),
      );
      const result = await crucible.processInput(input, "ANALYST", {
        agentId: channelId,
        agentContext,
      });

      if (result.error && isTimeoutErrorText(String(result.error))) {
        navigate("/", {
          replace: true,
          state: { timedOut: true, timeoutReason: String(result.error) },
        });
        return;
      }

      if (!isLatestChannelRevision(channelId, requestRevision)) {
        return;
      }

      setApiError(result.error ? String(result.error) : null);
      if (isLatestGlobalRevision(requestRevision)) {
        applyUpdatedDoc(result.updatedDoc, requestRevision);
      }

      // Response goes to the specialist's channel
      addMessage(
        channelId,
        "agent",
        result.response,
        500,
        10,
        result.error ? "error" : undefined,
        undefined,
        requestRevision,
      );
      if (!result.error) sendAnalyticsEvent("beat.complete", { channel: channelId });
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      if (isTimeoutErrorText(msg)) {
        navigate("/", {
          replace: true,
          state: { timedOut: true, timeoutReason: msg },
        });
        return;
      }
      if (isLatestChannelRevision(channelId, requestRevision)) {
        sendAnalyticsEvent("plot_hole.detected", { channel: channelId, error: msg });
        setApiError(msg);
        addMessage(
          channelId,
          "agent",
          `⚠️ ${msg}`,
          0,
          10,
          "error",
          undefined,
          requestRevision,
        );
      }
    } finally {
      endRequest();
    }
  };

  // Helper to add messages with custom typing speed
  const addMessage = (
    channelId: AgentType,
    sender: "user" | "agent",
    text: string,
    delay = 0,
    typingSpeed = 10,
    variant?: "error",
    fromAgent?: AgentType,
    expectedRevision?: number,
  ) => {
    const messageId = createMessageId();
    setTimeout(() => {
      if (
        typeof expectedRevision === "number" &&
        !isLatestChannelRevision(channelId, expectedRevision)
      ) {
        return;
      }
      setChannels((prev) =>
        prev.map((c) => {
          if (c.id === channelId) {
            if (c.messages.some((message) => message.id === messageId))
              return c;
            const normalizedIncoming = text
              .replace(/\s+/g, " ")
              .trim()
              .toLowerCase();
            const lastMessage = c.messages[c.messages.length - 1];
            const isDuplicateOfLast =
              Boolean(lastMessage) &&
              lastMessage.sender === sender &&
              (lastMessage.fromAgent ?? undefined) ===
                (fromAgent ?? undefined) &&
              (lastMessage.variant ?? undefined) === (variant ?? undefined) &&
              lastMessage.text.replace(/\s+/g, " ").trim().toLowerCase() ===
                normalizedIncoming;
            if (isDuplicateOfLast) return c;
            return {
              ...c,
              messages: [
                ...c.messages,
                {
                  id: messageId,
                  sender,
                  text,
                  typingSpeed,
                  variant,
                  fromAgent,
                },
              ],
            };
          }
          return c;
        }),
      );
    }, delay);
  };

  const toggleChannel = (id: AgentType) => {
    setChannels((prev) =>
      prev.map((c) => ({
        ...c,
        isOpen: c.id === id ? !c.isOpen : false, // Exclusive toggle: close others
      })),
    );
  };

  const calendarUrl = import.meta.env.VITE_CALENDAR_URL as string | undefined;
  const isCalendarReady = Boolean(calendarUrl);

  // Feedback: dispatch custom event that the global FeedbackWidget listens for
  const handleFeedbackClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    window.dispatchEvent(new CustomEvent("open-feedback-widget"));
    sendAnalyticsEvent("captive_portal_link", { label: "feedback" });
  };

  const handleCalendarClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isCalendarReady) {
      event.preventDefault();
      return;
    }
    sendAnalyticsEvent("captive_portal_link", { label: "calendar" });
  };

  // NEW: Handle card clicks from Eisenboard → toggle accordion
  const handleCardClick = (domain: Domain) => {
    const agentId = domainToAgent[domain.toUpperCase()];
    if (agentId) {
      // Toggle the selected agent's channel (open if closed, close if open)
      setChannels((prev) => {
        const currentChannel = prev.find((c) => c.id === agentId);
        const isCurrentlyOpen = currentChannel?.isOpen ?? false;

        return prev.map(
          (c) =>
            c.id === agentId
              ? { ...c, isOpen: !isCurrentlyOpen } // Toggle
              : c.id === "orchestrator"
                ? c // Keep Nova open
                : { ...c, isOpen: false }, // Close others
        );
      });
      // Highlight the selected domain card
      setSelectedDomain(selectedDomain === domain ? null : domain);
    }
  };

  // PRD14: load project by ID and restore latest snapshot to board
  const handleLoadProject = async (id: string) => {
    try {
      const project = await getProject(id, buildAuthHeader());
      if (!project) return;
      setBackendProjectId(project.id);
      setProjectSnapshots(project.snapshots ?? []);
      const snaps = project.snapshots ?? [];
      if (snaps.length > 0) {
        const latest = snaps[snaps.length - 1];
        const restored: Facet[] = latest.facets.map((f) => ({
          id: f.id,
          title: f.title,
          summary: f.summary,
          domain: (f.domain as Domain) || "STRATEGY",
          urgency: f.urgency,
          importance: f.importance,
          aiAnalysis: [],
        }));
        setActiveFacets(restored);
      }
    } catch {
      // silently ignore load errors
    }
  };

  // PRD14: restore a specific snapshot version to the board
  const handleSelectSnapshot = (snap: ProjectSnapshot) => {
    const restored: Facet[] = snap.facets.map((f) => ({
      id: f.id,
      title: f.title,
      summary: f.summary,
      domain: (f.domain as Domain) || "STRATEGY",
      urgency: f.urgency,
      importance: f.importance,
      aiAnalysis: [],
    }));
    setActiveFacets(restored);
  };

  const graphProblemTitle =
    (typeof initialQuery === "string" && initialQuery.trim()) ||
    channels
      .flatMap((channel) => channel.messages)
      .filter((message) => message.sender === "user")
      .map((message) => message.text.replace(/\s+/g, " ").trim())
      .find((text) => text.length > 24) ||
    "Current Challenge";

  return (
    <div className="flex h-screen w-full bg-[#09090b] text-white overflow-hidden font-sans isolate">
      {isHydrated && <div id="crucible-ready" style={{ display: "none" }} />}
      {/* Multi-Channel Sidebar */}
      <GambitSidebar
        initialQuery={initialQuery}
        activeChannels={channels}
        onToggleChannel={toggleChannel}
        onSendMessage={handleSendMessage}
        sendLocked={manualModeActive}
        sendLockMessage={lockReason}
        unlockLabel={user ? "Refresh access" : "Sign in with Google"}
        onRequestUnlock={user ? () => void syncAccessState() : signInWithGoogle}
        onGambitClick={() => {
          setIsGambitOpen(true);
          sendAnalyticsEvent("gambit_open", { source: "sidebar" });
        }}
      />

        {manualModeActive && (
          <div
            data-testid="manual-mode-banner"
            className="mx-6 mt-6 rounded-lg border border-amber-400/20 bg-amber-500/10 px-4 py-3 backdrop-blur-sm"
          >
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="text-sm font-semibold text-amber-200">
                  Manual board mode is active
                </div>
                <div className="text-xs text-amber-100/80">
                  {accessState === "signed_in_nonalpha"
                    ? "Your account is signed in but not on the alpha allowlist yet. Chat is locked; the board stays available for review and organization."
                    : "You’ve used the free preview. Chat is now locked until you sign in and your account is approved, but your board remains available."}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="manual-mode-access-action"
                  onClick={user ? () => void syncAccessState() : signInWithGoogle}
                  className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white hover:bg-white/10 transition-colors"
                >
                  {user ? "Refresh access" : "Sign in with Google"}
                </button>
                <button
                  type="button"
                  data-testid="manual-mode-details"
                  onClick={() => setShowCaptivePortal(true)}
                  className="rounded-lg border border-amber-300/20 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-100 hover:bg-amber-500/20 transition-colors"
                >
                  View access details
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Main Area */}
      <div className="flex-1 relative flex flex-col h-full overflow-hidden bg-[#0a0f1c]">
        {/* Top Bar */}
        <div
          data-testid="crucible-top-bar"
          className="h-14 border-b border-white/5 relative flex items-center px-6 bg-black/20 backdrop-blur-sm justify-between"
        >
          {/* Left: Global Navigation / Logo Placeholder */}
          <div className="flex items-center gap-2">
            {/* Maybe a small icon or just empty? User said "have just the navigation" */}
            <div className="w-8 h-8 bg-white/5 rounded flex items-center justify-center text-xs font-bold text-slate-500">
              IW
            </div>
          </div>

          {/* Right: Navigation Links + Snapshot Drawer */}
          <div className="flex items-center gap-4">
            <SnapshotDrawer
              projectId={backendProjectId}
              snapshots={projectSnapshots}
              onSelectSnapshot={handleSelectSnapshot}
            />
            <div className="flex gap-6 text-xs font-mono text-slate-500 uppercase tracking-widest font-bold">
              <span
                data-testid="nav-dashboard"
                className="hover:text-white cursor-pointer transition-colors"
              >
                Dashboard
              </span>
              <span
                data-testid="nav-settings"
                className="hover:text-white cursor-pointer transition-colors"
              >
                Settings
              </span>
              <span
                data-testid="nav-logout"
                className="hover:text-white cursor-pointer transition-colors"
              >
                Logout
              </span>
            </div>
          </div>
        </div>

        {/* API Error Banner */}
        {isTyping && (
          <div className="mx-6 mt-6 p-4 bg-cyan-900/10 border border-cyan-500/30 rounded-lg backdrop-blur-sm animate-in fade-in-50">
            <div className="flex items-center gap-3">
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse delay-100" />
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse delay-200" />
              </div>
              <p className="text-sm text-cyan-200 font-medium">
                Neural processing...
              </p>
            </div>
          </div>
        )}

        {apiError && (
          <div
            data-testid="api-error-banner"
            className="mx-6 mt-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg backdrop-blur-sm"
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl mt-0.5">⚠️</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-red-200 font-medium">{apiError}</p>
                {apiError.includes("rate limit") && (
                  <p className="text-xs text-red-300/70 mt-1">
                    {apiError.includes("daily")
                      ? "Daily quota exhausted — try again tomorrow or sign in for higher limits."
                      : "Too many requests — wait a moment then try again."}
                  </p>
                )}
                {apiError.includes("session limit") && (
                  <p className="text-xs text-red-300/70 mt-1">
                    Session question limit reached. Start a new session to
                    continue co-thinking.
                  </p>
                )}
              </div>
              <button
                onClick={() => setApiError(null)}
                className="text-red-400 hover:text-red-200 text-xl leading-none shrink-0"
                aria-label="Dismiss error"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* The Board */}
        <div className="flex-1 p-6 overflow-auto relative">
          {activeFacets.length >= 2 && questionCount >= 3 && !diagnosisDismissed && (
            <StratumDiagnosis
              facets={activeFacets}
              userName={normalizedUserName}
              problemTitle={graphProblemTitle}
              onDismiss={() => setDiagnosisDismissed(true)}
            />
          )}
          <Eisenboard
            facets={activeFacets}
            onCardClick={handleCardClick}
            problemTitle={graphProblemTitle}
            selectedDomain={selectedDomain}
            questionCount={questionCount}
            maxQuestions={maxQuestions}
          />
          <GambitChatWidget
            isOpen={isGambitOpen}
            onRequestOpen={() => setIsGambitOpen(true)}
            onRequestClose={() => setIsGambitOpen(false)}
          />
        </div>

        {/* PRD14: Project persistence panel */}
        <ProjectPanel
          projectId={backendProjectId}
          snapshots={projectSnapshots}
          onLoad={handleLoadProject}
          onSelectSnapshot={handleSelectSnapshot}
        />
      </div>

      {/* Captive Portal / Email Gate (PRD17: 3-send preview limit) */}
      {showCaptivePortal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in-50"
          data-testid="captive-portal"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Unlock full access"
            className="w-full max-w-md mx-4 bg-gradient-to-b from-[#0d1117] to-[#0a0f1c] border border-white/10 rounded-2xl p-8 shadow-2xl"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                <span className="text-3xl">🔒</span>
              </div>
              {accessState === "signed_in_nonalpha" ? (
                <>
                  <h2
                    className="text-xl font-bold text-white mb-2"
                    data-testid="captive-portal-manual-mode"
                  >
                    Manual mode unlocked — AI still gated
                  </h2>
                  <p className="text-sm text-slate-400">
                    You’re signed in, but this account is not yet on the alpha allowlist. You can keep inspecting and organizing the board while chat stays locked.
                  </p>
                </>
              ) : captiveEmailSubmitted ? (
                <>
                  <h2
                    className="text-xl font-bold text-white mb-2"
                    data-testid="captive-portal-manual-mode"
                  >
                    You’re in — we review manually
                  </h2>
                  <p className="text-sm text-slate-400 mb-3">
                    Usually 24–48 hours. You can keep the Crucible session going in manual mode while you wait, or explore the {"{a}OS7"} framework this tool is built on.
                  </p>
                  <a
                    href="https://aOS7.dev"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                  >
                    <span>See how AI teams diagnose their stack</span>
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                  </a>
                </>
              ) : (
                <>
                  <h2 className="text-xl font-bold text-white mb-2">
                    You've hit the preview limit
                  </h2>
                  <p className="text-sm text-slate-400">
                    This app is in alpha, built with love and care. It's your
                    app as much as mine — hit the feedback button below-left and
                    tell me exactly what you want, your must-have feature, and
                    I'll most definitely build it for you. Want to chat? Book a
                    short call with the link below-right at your convenience.
                  </p>
                </>
              )}
            </div>
            {accessState === "anonymous" && !captiveEmailSubmitted && (
              <>
                {/* Auth method tabs */}
                <div className="flex rounded-lg border border-white/10 overflow-hidden mb-4">
                  <button
                    type="button"
                    onClick={() => setAuthTab("google")}
                    className={`flex-1 py-2 text-xs font-semibold transition-colors ${authTab === "google" ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`}
                  >
                    Google
                  </button>
                  <button
                    type="button"
                    onClick={() => setAuthTab("email")}
                    className={`flex-1 py-2 text-xs font-semibold transition-colors ${authTab === "email" ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`}
                  >
                    Email link
                  </button>
                </div>

                {authTab === "google" ? (
                  <div className="space-y-4">
                    <input
                      type="email"
                      placeholder="Enter your email..."
                      value={captiveEmail}
                      onChange={(e) => setCaptiveEmail(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleCaptiveSubmit()
                      }
                      autoFocus
                      aria-label="Email address"
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50 transition-colors"
                    />
                    <button
                      onClick={handleCaptiveSubmit}
                      disabled={captiveSubmitting || !captiveEmail.includes("@")}
                      className="w-full py-3 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {captiveSubmitting ? "Submitting..." : "Join the Alpha"}
                    </button>
                    <p className="text-center text-[10px] text-slate-600">
                      We'll only use this to keep your session. No spam.
                    </p>
                  </div>
                ) : emailLinkSent ? (
                  <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200 text-center">
                    Check your inbox — a sign-in link is on its way.
                    <p className="mt-1 text-[11px] text-emerald-300/60">Click it on this device to finish signing in.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={otpEmail}
                      onChange={(e) => setOtpEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && otpEmail.includes("@") && !otpSending) {
                          setOtpSending(true);
                          void sendEmailLink(otpEmail).finally(() => setOtpSending(false));
                        }
                      }}
                      autoFocus
                      aria-label="Email for sign-in link"
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50 transition-colors"
                    />
                    <button
                      type="button"
                      disabled={otpSending || !otpEmail.includes("@")}
                      onClick={() => {
                        setOtpSending(true);
                        void sendEmailLink(otpEmail).finally(() => setOtpSending(false));
                      }}
                      className="w-full py-3 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {otpSending ? "Sending…" : "Send sign-in link"}
                    </button>
                    <p className="text-center text-[10px] text-slate-600">
                      No password needed. We'll email a one-click link.
                    </p>
                  </div>
                )}
              </>
            )}

            {(captiveEmailSubmitted || accessState === "signed_in_nonalpha") && (
              <div className="space-y-3">
                <button
                  type="button"
                  data-testid="captive-portal-signin"
                  onClick={user ? () => void syncAccessState() : signInWithGoogle}
                  className="w-full rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 px-4 py-3 text-sm font-bold text-white transition-all hover:from-cyan-500 hover:to-blue-500"
                >
                  {user ? "Refresh access" : "Sign in with Google"}
                </button>
                <button
                  type="button"
                  data-testid="captive-portal-continue"
                  onClick={() => setShowCaptivePortal(false)}
                  className="w-full rounded-lg border border-white/10 px-4 py-2 text-xs text-slate-300 transition-colors hover:border-white/30 hover:text-white"
                >
                  Continue in manual mode
                </button>
              </div>
            )}

            <div className="mt-5 flex items-center justify-between text-[11px]">
              <a
                data-testid="captive-feedback-button"
                href="#"
                onClick={handleFeedbackClick}
                target="_blank"
                rel="noreferrer"
                className="font-semibold transition-colors text-cyan-300 hover:text-cyan-200"
                title="Send feedback"
              >
                Feedback
              </a>
              <a
                data-testid="captive-calendar-button"
                href={calendarUrl || "#"}
                onClick={handleCalendarClick}
                aria-disabled={!isCalendarReady}
                target="_blank"
                rel="noreferrer"
                className={`font-semibold transition-colors ${isCalendarReady ? "text-slate-200 hover:text-white" : "text-slate-600 cursor-not-allowed"}`}
                title={
                  isCalendarReady
                    ? "Schedule a call"
                    : "Calendar link not configured"
                }
              >
                Schedule a call
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CruciblePage;
